import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Timer } from 'lucide-react';
import { useState } from 'react';

/** One-tap presets covering the most common hunt time limits. */
const MINUTE_PRESETS = [15, 30, 60] as const;

const MS_PER_MINUTE = 60_000;

export interface SetTimeLimitControlProps {
  eventId: string;
}

/**
 * Live-tab control for events running without an `endTime`: lets the
 * facilitator set the time remaining mid-game — quick minute presets,
 * a free "minutes left" input (endTime = now + minutes), or an exact
 * end time. Saves through the standard events PUT, so every countdown
 * surface (admin live tab, mobile stats poll) picks the new end up
 * through the usual refetch path.
 *
 * The caller decides *when* this renders (running event, no end time) —
 * see LiveStatusBar.
 */
export function SetTimeLimitControl({ eventId }: SetTimeLimitControlProps) {
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState('');
  const [exactEnd, setExactEnd] = useState('');
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (endTime: Date) =>
      apiClient.put(`/events/${eventId}`, { endTime: endTime.toISOString() }),
    onSuccess: (_response, endTime) => {
      queryClient.invalidateQueries({ queryKey: ['Event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['Event'] });
      toast.success(
        `Time limit set — event ends at ${endTime.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        })}`,
      );
      setOpen(false);
      setMinutes('');
      setExactEnd('');
    },
    onError: () => toast.error('Failed to set the time limit'),
  });

  const parsedMinutes = Number(minutes);
  const minutesValid = Number.isInteger(parsedMinutes) && parsedMinutes > 0;

  const exactEndDate = exactEnd ? new Date(exactEnd) : null;
  const exactEndValid =
    exactEndDate !== null && !Number.isNaN(exactEndDate.getTime()) && exactEndDate > new Date();

  const submitMinutes = (value: number) => {
    mutation.mutate(new Date(Date.now() + value * MS_PER_MINUTE));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Timer className="h-4 w-4" />
          Set time limit
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-4">
        <div className="space-y-1">
          <p className="text-sm font-medium">Set time remaining</p>
          <p className="text-xs text-muted-foreground">
            This event has no end time, so participants see no countdown. Set one to start the
            clock everywhere.
          </p>
        </div>

        <div className="flex gap-2">
          {MINUTE_PRESETS.map((preset) => (
            <Button
              key={preset}
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              disabled={mutation.isPending}
              onClick={() => submitMinutes(preset)}
            >
              {preset} min
            </Button>
          ))}
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="time-limit-minutes" className="text-xs">
              Minutes left
            </Label>
            <Input
              id="time-limit-minutes"
              type="number"
              min={1}
              placeholder="e.g. 45"
              value={minutes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinutes(e.target.value)}
            />
          </div>
          <Button
            type="button"
            size="sm"
            disabled={!minutesValid || mutation.isPending}
            onClick={() => submitMinutes(parsedMinutes)}
          >
            Set
          </Button>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="time-limit-exact" className="text-xs">
              Or exact end time
            </Label>
            <Input
              id="time-limit-exact"
              type="datetime-local"
              value={exactEnd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExactEnd(e.target.value)}
            />
          </div>
          <Button
            type="button"
            size="sm"
            disabled={!exactEndValid || mutation.isPending}
            onClick={() => {
              // Guarded by `exactEndValid`, so the date is present and future.
              if (exactEndDate) mutation.mutate(exactEndDate);
            }}
          >
            Set
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
